import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { ArrowLeft, Loader2, EyeOff, Globe, Crown, X, Check } from "lucide-react";
import api, { fmtError } from "@/lib/api";

/**
 * People Preferences — feeds the /swipe/deck filter.
 * Currently: preferred_gender + age range + hide_profile toggle + passport (VIP).
 */
const GENDERS = [
  { key: "everyone", i18n: "peoplePrefs.everyone" },
  { key: "woman",    i18n: "peoplePrefs.women" },
  { key: "man",      i18n: "peoplePrefs.men" },
  { key: "other",    i18n: "peoplePrefs.other" },
];

// Popular cities for one-tap Passport selection. Deliberately kept as a static
// list — a 3rd-party geo API would add cost and latency for a feature this simple.
// Users can still type a custom location in the input field.
const POPULAR_CITIES = [
  { key: "paris",       label: "Paris, France",              flag: "🇫🇷" },
  { key: "london",      label: "London, UK",                 flag: "🇬🇧" },
  { key: "new_york",    label: "New York, USA",              flag: "🇺🇸" },
  { key: "los_angeles", label: "Los Angeles, USA",           flag: "🇺🇸" },
  { key: "tokyo",       label: "Tokyo, Japan",               flag: "🇯🇵" },
  { key: "seoul",       label: "Seoul, South Korea",         flag: "🇰🇷" },
  { key: "berlin",      label: "Berlin, Germany",            flag: "🇩🇪" },
  { key: "madrid",      label: "Madrid, Spain",              flag: "🇪🇸" },
  { key: "barcelona",   label: "Barcelona, Spain",           flag: "🇪🇸" },
  { key: "rome",        label: "Rome, Italy",                flag: "🇮🇹" },
  { key: "milan",       label: "Milan, Italy",               flag: "🇮🇹" },
  { key: "amsterdam",   label: "Amsterdam, Netherlands",     flag: "🇳🇱" },
  { key: "stockholm",   label: "Stockholm, Sweden",          flag: "🇸🇪" },
  { key: "copenhagen",  label: "Copenhagen, Denmark",        flag: "🇩🇰" },
  { key: "oslo",        label: "Oslo, Norway",               flag: "🇳🇴" },
  { key: "helsinki",    label: "Helsinki, Finland",          flag: "🇫🇮" },
  { key: "lisbon",      label: "Lisbon, Portugal",           flag: "🇵🇹" },
  { key: "dublin",      label: "Dublin, Ireland",            flag: "🇮🇪" },
  { key: "vienna",      label: "Vienna, Austria",            flag: "🇦🇹" },
  { key: "prague",      label: "Prague, Czechia",            flag: "🇨🇿" },
  { key: "istanbul",    label: "Istanbul, Türkiye",          flag: "🇹🇷" },
  { key: "dubai",       label: "Dubai, UAE",                 flag: "🇦🇪" },
  { key: "mumbai",      label: "Mumbai, India",              flag: "🇮🇳" },
  { key: "bangkok",     label: "Bangkok, Thailand",          flag: "🇹🇭" },
  { key: "singapore",   label: "Singapore",                  flag: "🇸🇬" },
  { key: "sydney",      label: "Sydney, Australia",          flag: "🇦🇺" },
  { key: "sao_paulo",   label: "São Paulo, Brazil",          flag: "🇧🇷" },
  { key: "rio",         label: "Rio de Janeiro, Brazil",     flag: "🇧🇷" },
  { key: "mexico_city", label: "Mexico City, Mexico",        flag: "🇲🇽" },
  { key: "toronto",     label: "Toronto, Canada",            flag: "🇨🇦" },
];

export default function SettingsPeoplePrefs() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const [prefs, setPrefs] = useState({ preferred_gender: "everyone", age_min: 18, age_max: 99, hide_profile: false, passport_location: "" });
  const [recentCities, setRecentCities] = useState([]);
  const [isVip, setIsVip] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const refresh = async () => {
    try {
      const { data } = await api.get("/auth/me");
      setPrefs({
        preferred_gender: data.swipe_prefs?.preferred_gender || "everyone",
        age_min: data.swipe_prefs?.age_min ?? 18,
        age_max: data.swipe_prefs?.age_max ?? 99,
        hide_profile: !!data.swipe_prefs?.hide_profile,
        passport_location: data.swipe_prefs?.passport_location || "",
      });
      setRecentCities(data.swipe_prefs?.recent_cities || []);
      // Derive VIP from the fresh /auth/me — so if the user upgrades in
      // another tab and returns here, the Passport section unlocks after a
      // page focus without a full reload.
      setIsVip(!!(data.vip || data.is_vip));
    } catch (e) { toast.error(fmtError(e.response?.data?.detail) || e.message); }
    finally { setLoading(false); }
  };

  useEffect(() => { refresh(); }, []);

  // Re-fetch on window focus so a VIP upgrade in another tab reflects here.
  useEffect(() => {
    const onFocus = () => refresh();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, []);

  const save = async (next) => {
    setPrefs(next);
    setSaving(true);
    try { await api.patch("/users/me", { swipe_prefs: next }); }
    catch (e) { toast.error(fmtError(e.response?.data?.detail) || e.message); }
    finally { setSaving(false); }
  };

  // Passport uses a dedicated endpoint so the backend maintains the rolling
  // recent_cities list. Falls back to setPrefs so the UI updates immediately.
  const savePassport = async (location) => {
    setPrefs((p) => ({ ...p, passport_location: location }));
    setSaving(true);
    try {
      const { data } = await api.post("/swipe/passport", { location });
      setRecentCities(data.recent_cities || []);
    } catch (e) { toast.error(fmtError(e.response?.data?.detail) || e.message); }
    finally { setSaving(false); }
  };

  return (
    <div className="min-h-screen bg-white text-[#0A0B0D] max-w-2xl mx-auto pb-32" data-testid="people-prefs-page">
      <div className="sticky top-0 z-30 bg-white border-b border-[#E2DFD4]">
        <div className="flex items-center gap-2 px-5 py-5">
          <button onClick={() => nav(-1)} className="w-10 h-10 -ml-2 flex items-center justify-center rounded-full hover:bg-[#F4F1E8]" data-testid="prefs-back" aria-label="Back">
            <ArrowLeft className="w-7 h-7" strokeWidth={3} />
          </button>
          <h1 className="font-display font-black text-[26px] tracking-tight">{t("settings.peoplePrefs")}</h1>
          {saving && <Loader2 className="w-4 h-4 animate-spin text-[#7C828D] ml-2" />}
        </div>
      </div>

      {loading ? (
        <div className="p-10 text-center"><Loader2 className="w-6 h-6 animate-spin inline text-[#7C828D]" /></div>
      ) : (
        <div className="px-5 pt-6 space-y-8">
          {/* Show me */}
          <section data-testid="prefs-gender-section">
            <h2 className="font-display font-black text-lg mb-3">{t("peoplePrefs.showMe")}</h2>
            <div className="grid grid-cols-2 gap-2">
              {GENDERS.map((g) => (
                <button
                  key={g.key}
                  onClick={() => save({ ...prefs, preferred_gender: g.key })}
                  data-testid={`gender-${g.key}`}
                  className={`px-4 py-3 rounded-2xl font-bold border-2 transition-all ${prefs.preferred_gender === g.key ? "bg-[#00CED1] border-[#121212] shadow-[3px_3px_0_#121212]" : "bg-[#F4F1E8] border-[#E2DFD4] text-[#3D424B]"}`}
                >
                  {t(g.i18n)}
                </button>
              ))}
            </div>
          </section>

          {/* Age range */}
          <section data-testid="prefs-age-section">
            <h2 className="font-display font-black text-lg mb-1">{t("peoplePrefs.ageRange")}</h2>
            <p className="text-sm text-[#7C828D] mb-3">{prefs.age_min}–{prefs.age_max}</p>
            <div className="space-y-3">
              <label className="text-xs font-bold text-[#7C828D] uppercase tracking-wide">{t("peoplePrefs.min")}</label>
              <input
                type="range" min="18" max="99" value={prefs.age_min}
                onChange={(e) => save({ ...prefs, age_min: Math.min(parseInt(e.target.value, 10), prefs.age_max) })}
                className="w-full accent-[#00CED1]" data-testid="age-min-slider"
              />
              <label className="text-xs font-bold text-[#7C828D] uppercase tracking-wide">{t("peoplePrefs.max")}</label>
              <input
                type="range" min="18" max="99" value={prefs.age_max}
                onChange={(e) => save({ ...prefs, age_max: Math.max(parseInt(e.target.value, 10), prefs.age_min) })}
                className="w-full accent-[#00CED1]" data-testid="age-max-slider"
              />
            </div>
          </section>

          {/* Hide profile toggle */}
          <section data-testid="prefs-hide-section">
            <div className="flex items-start gap-4 py-3">
              <EyeOff className="w-5 h-5 text-[#3D424B] mt-1 shrink-0" strokeWidth={2.5} />
              <div className="flex-1">
                <div className="font-display font-black text-lg">{t("peoplePrefs.hideProfile")}</div>
                <p className="text-sm text-[#7C828D]">{t("peoplePrefs.hideProfileHint")}</p>
              </div>
              <button
                onClick={() => save({ ...prefs, hide_profile: !prefs.hide_profile })}
                role="switch"
                aria-checked={prefs.hide_profile}
                data-testid="hide-toggle"
                className={`relative w-12 h-7 rounded-full transition-colors mt-1 ${prefs.hide_profile ? "bg-[#00CED1]" : "bg-[#D8D3C2]"}`}
              >
                <span className={`absolute top-0.5 left-0.5 w-6 h-6 rounded-full bg-white shadow transition-transform ${prefs.hide_profile ? "translate-x-5" : "translate-x-0"}`} />
              </button>
            </div>
          </section>

          {/* Passport (VIP) */}
          <PassportSection
            t={t}
            nav={nav}
            isVip={isVip}
            value={prefs.passport_location || ""}
            recentCities={recentCities}
            onSave={savePassport}
          />
        </div>
      )}
    </div>
  );
}

function PassportSection({ t, nav, isVip, value, recentCities, onSave }) {
  const [draft, setDraft] = useState(value);
  useEffect(() => { setDraft(value); }, [value]);
  const dirty = draft.trim() !== value.trim();

  const apply = () => onSave(draft.trim());
  const clear = () => { setDraft(""); onSave(""); };
  // One-tap select: fills the input AND saves immediately (no extra Apply tap).
  const pickCity = (label) => { setDraft(label); onSave(label); };

  return (
    <section data-testid="prefs-passport-section" className="pb-6">
      <div className="flex items-start gap-4 py-3">
        <Globe className="w-5 h-5 text-[#3D424B] mt-1 shrink-0" strokeWidth={2.5} />
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="font-display font-black text-lg">{t("peoplePrefs.passportTitle")}</div>
            <span className="inline-flex items-center gap-1 bg-[#B49CFF] text-white text-[10px] font-black px-2 py-0.5 rounded-full uppercase tracking-wide">
              <Crown className="w-3 h-3" strokeWidth={3} /> VIP
            </span>
          </div>
          <p className="text-sm text-[#7C828D]">{t("peoplePrefs.passportHint")}</p>
        </div>
      </div>

      {!isVip ? (
        <button
          onClick={() => nav("/vip")}
          className="w-full mt-2 bg-gradient-to-r from-[#B49CFF] to-[#8F72FF] text-white font-black py-3 rounded-2xl border-2 border-[#121212] shadow-[3px_3px_0_#121212] active:translate-y-[1px]"
          data-testid="passport-vip-upsell"
        >
          {t("peoplePrefs.passportUpsell")}
        </button>
      ) : (
        <div className="mt-2 space-y-3">
          {/* Recently used — quick access to the last 5 chosen Passport cities */}
          {recentCities && recentCities.length > 0 && (
            <div data-testid="passport-recent-cities">
              <label className="text-xs font-black text-[#7C828D] uppercase tracking-wide mb-2 block">
                {t("peoplePrefs.passportRecent")}
              </label>
              <div className="flex flex-wrap gap-2">
                {recentCities.map((c) => {
                  const active = draft.trim() === c;
                  return (
                    <button
                      key={c}
                      onClick={() => pickCity(c)}
                      data-testid={`passport-recent-${encodeURIComponent(c)}`}
                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border-2 text-xs font-bold transition-all active:scale-95 ${
                        active
                          ? "bg-[#B49CFF] text-white border-[#121212] shadow-[2px_2px_0_#121212]"
                          : "bg-[#F4F1E8] text-[#3D424B] border-[#B49CFF]/40 hover:border-[#B49CFF]"
                      }`}
                    >
                      <span aria-hidden>⏱️</span>
                      <span className="truncate max-w-[140px]">{c.split(",")[0]}</span>
                      {active && <Check className="w-3 h-3" strokeWidth={3} />}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Popular cities — one-tap select */}
          <div>
            <label className="text-xs font-black text-[#7C828D] uppercase tracking-wide mb-2 block">{t("peoplePrefs.passportPopular")}</label>
            <div className="flex flex-wrap gap-2" data-testid="passport-popular-cities">
              {POPULAR_CITIES.map((c) => {
                // Follow the draft (what the user is currently editing) so the
                // chip visually deactivates the moment they diverge to custom text.
                const active = draft.trim() === c.label;
                return (
                  <button
                    key={c.key}
                    onClick={() => pickCity(c.label)}
                    data-testid={`passport-city-${c.key}`}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border-2 text-xs font-bold transition-all active:scale-95 ${
                      active
                        ? "bg-[#B49CFF] text-white border-[#121212] shadow-[2px_2px_0_#121212]"
                        : "bg-white text-[#3D424B] border-[#E2DFD4] hover:border-[#B49CFF]"
                    }`}
                  >
                    <span aria-hidden>{c.flag}</span>
                    <span>{c.label.split(",")[0]}</span>
                    {active && <Check className="w-3 h-3" strokeWidth={3} />}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Free-text fallback */}
          <div>
            <label className="text-xs font-black text-[#7C828D] uppercase tracking-wide mb-2 block">{t("peoplePrefs.passportCustom")}</label>
            <div className="relative">
              <input
                type="text"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder={t("peoplePrefs.passportPlaceholder")}
                className="w-full bg-[#F4F1E8] border-2 border-[#E2DFD4] rounded-2xl px-4 py-3 pr-10 font-semibold text-sm focus:outline-none focus:border-[#00CED1]"
                data-testid="passport-input"
                maxLength={80}
              />
              {draft && (
                <button
                  onClick={clear}
                  className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full hover:bg-black/5 flex items-center justify-center"
                  aria-label="Clear"
                  data-testid="passport-clear"
                >
                  <X className="w-4 h-4 text-[#7C828D]" strokeWidth={2.5} />
                </button>
              )}
            </div>
          </div>

          <button
            onClick={apply}
            disabled={!dirty}
            className="w-full bg-[#00CED1] text-black font-black py-3 rounded-2xl border-2 border-[#121212] shadow-[3px_3px_0_#121212] active:translate-y-[1px] disabled:opacity-40 disabled:cursor-not-allowed disabled:shadow-none"
            data-testid="passport-save"
          >
            {t("peoplePrefs.passportApply")}
          </button>
        </div>
      )}
    </section>
  );
}
